import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Space,
  message,
  Card,
  Tag,
  Input,
  Select,
  Typography,
} from "antd";
import {
  UserOutlined,
  SearchOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import apiClient from "../utils/apiClient";

const { Title } = Typography;
const { Option } = Select;

const SuperAdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/superadmin/dashboard");
      const data = response.data?.data || {};
      setUsers(data.users || []);
    } catch (error) {
      message.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchText.toLowerCase()) ||
      user.companyId?.toLowerCase().includes(searchText.toLowerCase());

    const matchesRole = roleFilter === "all" || user.role === roleFilter;

    return matchesSearch && matchesRole;
  });

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role) => {
        const colors = {
          admin: "purple",
          salesManager: "blue",
          teamLead: "cyan",
          advisor: "green",
          accounts: "orange",
          reservations: "magenta",
          transport: "gold",
        };
        return <Tag color={colors[role] || "default"}>{role}</Tag>;
      },
    },
    {
      title: "Company ID",
      dataIndex: "companyId",
      key: "companyId",
      render: (id) => <Tag>{id || "N/A"}</Tag>,
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <Space>
          <Tag color={record.isActive ? "green" : "red"}>
            {record.isActive ? "Active" : "Inactive"}
          </Tag>
          {record.isVerified && <Tag color="blue">Verified</Tag>}
        </Space>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (date ? new Date(date).toLocaleDateString() : "N/A"),
    },
  ];

  return (
    <div>
      <Title level={2}>
        <UserOutlined /> All Users Management
      </Title>

      <Card>
        <Space
          style={{
            marginBottom: 16,
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <Input
            placeholder="Search users by name, email, or company..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 400 }}
            size="large"
          />

          <Select
            value={roleFilter}
            onChange={setRoleFilter}
            style={{ width: 200 }}
            size="large"
            prefix={<FilterOutlined />}
          >
            <Option value="all">All Roles</Option>
            <Option value="admin">Admin</Option>
            <Option value="sales_manager">Sales Manager</Option>
            <Option value="team_lead">Team Lead</Option>
            <Option value="advisor">Advisor</Option>
            <Option value="accounts">Accounts</Option>
            <Option value="reservations">Reservations</Option>
            <Option value="transport">Transport</Option>
          </Select>
        </Space>

        <Table
          columns={columns}
          dataSource={filteredUsers}
          rowKey="_id"
          loading={loading}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} users`,
          }}
        />
      </Card>
    </div>
  );
};

export default SuperAdminUsers;
